import React, { useState, useMemo } from 'react';
import { X, Home, Search, Filter, Building2, MapPin, DollarSign, Eye } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import PropertyDetailsPopup from './PropertyDetailsPopup';

const PropertiesPopupModal = ({ isOpen, onClose, properties = [], userRole }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState(null);

  if (!isOpen) return null;

  const formatCurrency = (amount, currency) => {
    if (!amount) return 'غير محدد';
    const formatted = new Intl.NumberFormat('ar-EG')?.format(amount);
    return `${formatted} ${currency || 'د.أ'}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      'متاح': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'محجوز': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'مؤجر': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'صيانة': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      'معلق': 'bg-red-500/20 text-red-400 border-red-500/30',
      'مباع': 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    };
    return colors?.[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'شقة': 'Building2',
      'فيلا': 'Home',
      'مكتب': 'Briefcase',
      'محل': 'Store',
      'أرض': 'MapPin'
    };
    return icons?.[type] || 'Building2';
  };

  // Filter properties
  const filteredProperties = useMemo(() => {
    return properties?.filter((property) => {
      const matchesSearch = !searchTerm || 
        property?.address?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        property?.district?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        property?.propertyId?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        property?.owner?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || property?.status === statusFilter;
      const matchesType = typeFilter === 'all' || property?.type === typeFilter;
      
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [properties, searchTerm, statusFilter, typeFilter]);

  // Get unique statuses and types
  const uniqueStatuses = [...new Set(properties?.map(p => p?.status)?.filter(Boolean))];
  const uniqueTypes = [...new Set(properties?.map(p => p?.type)?.filter(Boolean))];

  const handlePropertyClick = (property) => {
    setSelectedProperty(property);
  };

  const handleCloseDetails = () => {
    setSelectedProperty(null);
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998] transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl sm:rounded-2xl shadow-2xl border border-amber-500/30 w-full max-w-7xl max-h-[95vh] flex flex-col transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-4 lg:p-6 border-b border-amber-500/20 flex-shrink-0">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex-shrink-0">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <div>
                <h2 className="text-base sm:text-xl lg:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
                  جميع العقارات
                </h2>
                <p className="text-[10px] sm:text-xs text-slate-400 mt-0.5">
                  {filteredProperties?.length} من {properties?.length} عقار
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 hover:bg-slate-700/50 rounded-lg transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
            </button>
          </div>

          {/* Search and Filters */}
          <div className="p-3 sm:p-4 lg:p-6 border-b border-slate-700/50 space-y-3 flex-shrink-0">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="ابحث عن عقار (العنوان، المنطقة، المعرف، المالك)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pr-10 pl-4 py-2 sm:py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all text-xs sm:text-sm"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 flex-shrink-0" />
                <span className="text-[10px] sm:text-xs text-slate-400">تصفية:</span>
              </div>
              
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-[10px] sm:text-xs focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all cursor-pointer"
              >
                <option value="all">كل الحالات</option>
                {uniqueStatuses?.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>

              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e?.target?.value)}
                className="px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-[10px] sm:text-xs focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all cursor-pointer"
              >
                <option value="all">كل الأنواع</option>
                {uniqueTypes?.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              {/* Clear Filters */}
              {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setTypeFilter('all');
                  }}
                  className="px-2 sm:px-3 py-1 sm:py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-red-400 text-[10px] sm:text-xs transition-all"
                >
                  مسح الفلاتر
                </button>
              )}
            </div>
          </div>

          {/* Properties Grid */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
            {filteredProperties?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-800/50 rounded-full flex items-center justify-center mb-4">
                  <Home className="w-8 h-8 sm:w-10 sm:h-10 text-slate-600" />
                </div>
                <p className="text-sm sm:text-base text-slate-400 text-center">
                  {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' ?'لا توجد عقارات تطابق معايير البحث' :'لا توجد عقارات متاحة'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {filteredProperties?.map((property) => (
                  <div
                    key={property?.id}
                    onClick={() => handlePropertyClick(property)}
                    className="group relative bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-lg overflow-hidden border border-slate-700/50 hover:border-amber-500/50 transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-amber-500/10 hover:-translate-y-1"
                  >
                    {/* Property Image */}
                    <div className="relative h-32 sm:h-36 lg:h-40 overflow-hidden bg-slate-800">
                      <Image
                        src={property?.image}
                        alt={property?.imageAlt || `صورة عقار ${property?.propertyId}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      
                      {/* Status Badge */}
                      <div className="absolute top-2 right-2">
                        <span className={`px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-medium border backdrop-blur-sm ${getStatusColor(property?.status)}`}>
                          {property?.status}
                        </span>
                      </div>

                      {/* View Overlay */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <div className="flex items-center gap-2 text-white">
                          <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="text-xs sm:text-sm font-medium">عرض التفاصيل</span>
                        </div>
                      </div>
                    </div>

                    {/* Property Info */}
                    <div className="p-2.5 sm:p-3 space-y-2">
                      {/* Property ID */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[9px] sm:text-[10px] font-data text-amber-400/80" dir="ltr">
                          {property?.propertyId}
                        </span>
                        <div className="flex items-center gap-1">
                          <Icon name={getTypeIcon(property?.type)} size={12} className="text-amber-500" />
                          <span className="text-[9px] sm:text-[10px] text-slate-400">{property?.type}</span>
                        </div>
                      </div>

                      {/* Address */}
                      <h3 className="text-xs sm:text-sm font-medium text-white line-clamp-1 leading-tight">
                        {property?.address}
                      </h3>

                      {/* District */}
                      <div className="flex items-center gap-1 text-slate-400">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="text-[10px] sm:text-xs line-clamp-1">{property?.district}</span>
                      </div>

                      {/* Price */}
                      <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-700/50">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-400" />
                          <span className="text-[10px] sm:text-xs text-slate-400">السعر:</span>
                        </div>
                        <span className="text-xs sm:text-sm font-semibold text-emerald-400 font-data" dir="ltr">
                          {formatCurrency(property?.asking_price || property?.price, property?.currency)}
                        </span>
                      </div>

                      {/* Owner */}
                      <div className="flex items-center gap-1.5 text-slate-400">
                        <Icon name="User" size={12} className="flex-shrink-0" />
                        <span className="text-[10px] sm:text-xs line-clamp-1">{property?.owner}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Property Details Popup */}
      {selectedProperty && (
        <PropertyDetailsPopup
          property={selectedProperty}
          onClose={handleCloseDetails}
          userRole={userRole}
        />
      )}
    </>
  );
};

export default PropertiesPopupModal;